import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  ADMIN_SEARCH_USERS,
  ADMIN_USER_DETAIL,
  ADMIN_USER_LEDGER,
  ADJUST_KOINS,
} from "../graphql";
import type {
  AdminSearchUsersResult,
  AdminUserDetail,
  AdminUserLedgerPage,
  KoinsSourceType,
} from "../types";

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useAdminSearchUsers(search: string, limit = 20, offset = 0) {
  return useQuery<{ adminSearchUsers: AdminSearchUsersResult }>(
    ADMIN_SEARCH_USERS,
    {
      variables: { search, limit, offset },
      skip: search.trim().length < 2,
      fetchPolicy: "cache-and-network",
    },
  );
}

export function useAdminSearchUsersLazy() {
  return useLazyQuery<{ adminSearchUsers: AdminSearchUsersResult }>(
    ADMIN_SEARCH_USERS,
    { fetchPolicy: "network-only" },
  );
}

export function useAdminUserDetail(userId: string) {
  return useQuery<{ adminUserDetail: AdminUserDetail }>(ADMIN_USER_DETAIL, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: "cache-and-network",
  });
}

export function useAdminUserLedger(
  userId: string,
  options?: { first?: number; after?: string; sourceType?: KoinsSourceType },
) {
  return useQuery<{ adminUserLedger: AdminUserLedgerPage }>(
    ADMIN_USER_LEDGER,
    {
      variables: {
        userId,
        first: options?.first ?? 20,
        after: options?.after,
        sourceType: options?.sourceType,
      },
      skip: !userId,
      fetchPolicy: "cache-and-network",
    },
  );
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useAdjustKoins() {
  return useMutation(ADJUST_KOINS, {
    refetchQueries: ["AdminUserDetail", "AdminUserLedger"],
  });
}
